/**
 * Custom IWT Slider — Dawn-compatible Web Component
 *
 * No Swiper / MinimogLibs required.
 * Pure vanilla JS with infinite-loop logic built in.
 *
 * Usage (automatic via custom element):
 *   <custom-iwt-slider data-autoplay="true" data-autoplay-speed="5">
 *     ...slides...
 *   </custom-iwt-slider>
 */

if (!customElements.get('custom-iwt-slider')) {
  class CustomIwtSlider extends HTMLElement {
    constructor() {
      super();
      this._index       = 0;  // current real index (0-based)
      this._total       = 0;
      this._autoplayTimer = null;
      this._isAnimating = false;
    }

    /* ── Lifecycle ────────────────────────────────── */

    connectedCallback() {
      this._autoplay      = this.dataset.autoplay === 'true';
      this._autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 5;

      this._imageSlides   = Array.from(this.querySelectorAll('.custom-iwt-slider__image-slide'));
      this._contentSlides = Array.from(this.querySelectorAll('.custom-iwt-slider__content-slide'));
      this._total         = this._imageSlides.length;

      if (this._total < 2) return; // nothing to wire up for a single slide

      this._btnPrev = this.querySelector('.custom-iwt-slider__btn--prev');
      this._btnNext = this.querySelector('.custom-iwt-slider__btn--next');

      this._bindEvents();
      this._goTo(0, false); // ensure first slide is correctly set up
      if (this._autoplay) this._startAutoplay();
    }

    disconnectedCallback() {
      this._stopAutoplay();
    }

    /* ── Events ───────────────────────────────────── */

    _bindEvents() {
      if (this._btnPrev) {
        this._btnPrev.addEventListener('click', () => this._prev());
      }
      if (this._btnNext) {
        this._btnNext.addEventListener('click', () => this._next());
      }

      // Pause autoplay on hover / focus inside the section
      this.addEventListener('mouseenter', () => this._stopAutoplay());
      this.addEventListener('mouseleave', () => {
        if (this._autoplay) this._startAutoplay();
      });
      this.addEventListener('focusin',  () => this._stopAutoplay());
      this.addEventListener('focusout', () => {
        if (this._autoplay) this._startAutoplay();
      });

      // Swipe / drag support
      this._addSwipeListeners();
    }

    /* ── Navigation ───────────────────────────────── */

    _prev() {
      if (this._isAnimating) return;
      this._goTo((this._index - 1 + this._total) % this._total, true, false);
    }

    _next() {
      if (this._isAnimating) return;
      this._goTo((this._index + 1) % this._total, true, true);
    }

    _goTo(index, animate = true, goingForward = true) {
      if (index === this._index && animate) return;

      const prevIndex   = this._index;
      const prevImage   = this._imageSlides[prevIndex];
      const prevContent = this._contentSlides[prevIndex];
      const nextImage   = this._imageSlides[index];
      const nextContent = this._contentSlides[index];

      this._index = index;

      if (!animate) {
        // Silent init — just set active classes
        if (prevImage)   prevImage.classList.remove('is-active');
        if (prevContent) { prevContent.classList.remove('is-active'); prevContent.setAttribute('aria-hidden', 'true'); }
        if (nextImage)   nextImage.classList.add('is-active');
        if (nextContent) { nextContent.classList.add('is-active'); nextContent.setAttribute('aria-hidden', 'false'); }
        return;
      }

      this._isAnimating = true;

      // ── Content: fade out immediately ──
      if (prevContent) {
        prevContent.classList.remove('is-active');
        prevContent.setAttribute('aria-hidden', 'true');
      }

      // ── Image: slide out the current ──
      if (prevImage) {
        prevImage.classList.remove('is-active');
        prevImage.classList.add(goingForward ? 'is-leaving-left' : 'is-leaving-right');
      }

      // ── Image: position the incoming slide off-screen instantly ──
      if (nextImage) {
        nextImage.classList.add(goingForward ? 'is-entering-right' : 'is-entering-left');
        // Force a reflow so the browser registers the off-screen position before transitioning
        nextImage.getBoundingClientRect();
        nextImage.classList.remove('is-entering-right', 'is-entering-left');
        nextImage.classList.add('is-active');
      }

      // ── Content: fade in after 0.5s delay (handled by CSS transition-delay) ──
      if (nextContent) {
        nextContent.classList.add('is-active');
        nextContent.setAttribute('aria-hidden', 'false');
      }

      // Clean up leaving classes after transition ends
      const cleanup = () => {
        if (prevImage) prevImage.classList.remove('is-leaving-left', 'is-leaving-right');
        this._isAnimating = false;
      };

      prevImage
        ? prevImage.addEventListener('transitionend', cleanup, { once: true })
        : (this._isAnimating = false);
    }

    /* ── Autoplay ─────────────────────────────────── */

    _startAutoplay() {
      this._stopAutoplay();
      this._autoplayTimer = setInterval(() => this._goTo((this._index + 1) % this._total, true, true), this._autoplaySpeed * 1000);
    }

    _stopAutoplay() {
      if (this._autoplayTimer) {
        clearInterval(this._autoplayTimer);
        this._autoplayTimer = null;
      }
    }

    /* ── Swipe support ────────────────────────────── */

    _addSwipeListeners() {
      let startX = null;

      const imageCol = this.querySelector('.custom-iwt-slider__image-col');
      if (!imageCol) return;

      imageCol.addEventListener('touchstart', (e) => {
        startX = e.changedTouches[0].clientX;
      }, { passive: true });

      imageCol.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const delta = e.changedTouches[0].clientX - startX;
        if (Math.abs(delta) > 40) {
          delta < 0 ? this._next() : this._prev();
        }
        startX = null;
      }, { passive: true });
    }
  }

  customElements.define('custom-iwt-slider', CustomIwtSlider);
}